import type { City, Color, Route, Ticket } from "./types";

// Original schematic coordinates; no publisher illustrations are distributed.
export const BOARD_WIDTH = 1100;
export const BOARD_HEIGHT = 760;
export const CITIES: City[] = [
  ["edinburgh", "Edinburgh", 195, 85],
  ["london", "London", 255, 235],
  ["lisboa", "Lisboa", 60, 650],
  ["cadiz", "Cádiz", 125, 705],
  ["madrid", "Madrid", 135, 620],
  ["barcelona", "Barcelona", 250, 635],
  ["pamplona", "Pamplona", 235, 540],
  ["brest", "Brest", 155, 365],
  ["dieppe", "Dieppe", 245, 330],
  ["paris", "Paris", 295, 380],
  ["bruxelles", "Bruxelles", 335, 285],
  ["amsterdam", "Amsterdam", 355, 235],
  ["essen", "Essen", 440, 245],
  ["frankfurt", "Frankfurt", 425, 325],
  ["berlin", "Berlin", 540, 260],
  ["kobenhavn", "København", 515, 140],
  ["stockholm", "Stockholm", 620, 60],
  ["danzig", "Danzig", 665, 180],
  ["riga", "Riga", 750, 90],
  ["petrograd", "Petrograd", 920, 85],
  ["moskva", "Moskva", 1020, 205],
  ["smolensk", "Smolensk", 935, 235],
  ["wilno", "Wilno", 830, 230],
  ["warszawa", "Warszawa", 715, 260],
  ["kyiv", "Kyiv", 865, 320],
  ["kharkov", "Kharkov", 1005, 375],
  ["rostov", "Rostov", 1050, 430],
  ["sochi", "Sochi", 1045, 530],
  ["erzurum", "Erzurum", 1030, 650],
  ["angora", "Angora", 935, 675],
  ["constantinople", "Constantinople", 860, 625],
  ["smyrna", "Smyrna", 815, 705],
  ["athina", "Athína", 730, 675],
  ["sofia", "Sofia", 745, 560],
  ["bucuresti", "București", 810, 490],
  ["budapest", "Budapest", 655, 420],
  ["wien", "Wien", 605, 390],
  ["munchen", "München", 485, 375],
  ["zurich", "Zürich", 415, 440],
  ["venezia", "Venezia", 505, 475],
  ["marseille", "Marseille", 390, 540],
  ["roma", "Roma", 515, 565],
  ["brindisi", "Brindisi", 600, 595],
  ["palermo", "Palermo", 550, 705],
  ["zagrab", "Zágráb", 590, 490],
  ["sarajevo", "Sarajevo", 675, 550],
  ["sevastopol", "Sevastopol", 950, 510],
].map(([id, name, x, y]) => ({ id, name, x, y })) as City[];

// Route facts transcribed against the board pictured in the publisher's standard Europe rulebook.
// Parallel routes have distinct IDs, even when their colors match.
type RouteRow = [string, string, number, Color | "gray", ("tunnel" | number)?];
const rows: RouteRow[] = [
  ["edinburgh", "london", 4, "black"],
  ["edinburgh", "london", 4, "orange"],
  ["london", "amsterdam", 2, "gray", 2],
  ["london", "dieppe", 2, "gray", 1],
  ["london", "dieppe", 2, "gray", 1],
  ["lisboa", "cadiz", 2, "blue"],
  ["lisboa", "madrid", 3, "purple"],
  ["cadiz", "madrid", 3, "orange"],
  ["madrid", "barcelona", 2, "yellow"],
  ["madrid", "pamplona", 3, "black", "tunnel"],
  ["madrid", "pamplona", 3, "white", "tunnel"],
  ["barcelona", "pamplona", 2, "gray", "tunnel"],
  ["barcelona", "marseille", 4, "gray"],
  ["pamplona", "marseille", 4, "red"],
  ["pamplona", "paris", 4, "blue"],
  ["pamplona", "paris", 4, "green"],
  ["pamplona", "brest", 4, "purple"],
  ["brest", "dieppe", 2, "orange"],
  ["brest", "paris", 3, "black"],
  ["dieppe", "paris", 1, "purple"],
  ["dieppe", "bruxelles", 2, "green"],
  ["paris", "bruxelles", 2, "yellow"],
  ["paris", "bruxelles", 2, "red"],
  ["paris", "frankfurt", 3, "white"],
  ["paris", "frankfurt", 3, "orange"],
  ["paris", "zurich", 3, "gray", "tunnel"],
  ["paris", "marseille", 4, "gray"],
  ["bruxelles", "amsterdam", 1, "black"],
  ["bruxelles", "frankfurt", 2, "blue"],
  ["amsterdam", "essen", 3, "yellow"],
  ["amsterdam", "frankfurt", 2, "white"],
  ["essen", "frankfurt", 2, "green"],
  ["essen", "berlin", 2, "blue"],
  ["essen", "kobenhavn", 3, "gray", 1],
  ["essen", "kobenhavn", 3, "gray", 1],
  ["frankfurt", "berlin", 3, "black"],
  ["frankfurt", "berlin", 3, "red"],
  ["frankfurt", "munchen", 2, "purple"],
  ["kobenhavn", "stockholm", 3, "yellow"],
  ["kobenhavn", "stockholm", 3, "white"],
  ["stockholm", "petrograd", 8, "gray", "tunnel"],
  ["riga", "petrograd", 4, "gray"],
  ["riga", "danzig", 3, "black"],
  ["riga", "wilno", 4, "green"],
  ["petrograd", "wilno", 4, "blue"],
  ["petrograd", "moskva", 4, "white"],
  ["berlin", "danzig", 4, "gray"],
  ["berlin", "warszawa", 4, "purple"],
  ["berlin", "warszawa", 4, "yellow"],
  ["berlin", "wien", 3, "green"],
  ["danzig", "warszawa", 2, "gray"],
  ["warszawa", "wilno", 3, "red"],
  ["warszawa", "kyiv", 4, "gray"],
  ["warszawa", "wien", 4, "blue"],
  ["wilno", "smolensk", 3, "yellow"],
  ["wilno", "kyiv", 2, "gray"],
  ["smolensk", "moskva", 2, "orange"],
  ["smolensk", "kyiv", 3, "red"],
  ["moskva", "kharkov", 4, "gray"],
  ["kyiv", "kharkov", 4, "gray"],
  ["kyiv", "bucuresti", 4, "gray"],
  ["kyiv", "budapest", 6, "gray", "tunnel"],
  ["kharkov", "rostov", 2, "green"],
  ["rostov", "sevastopol", 4, "gray"],
  ["rostov", "sochi", 2, "gray"],
  ["sochi", "sevastopol", 2, "gray", 1],
  ["sochi", "erzurum", 3, "red", "tunnel"],
  ["sevastopol", "erzurum", 4, "gray", 2],
  ["sevastopol", "constantinople", 4, "gray", 2],
  ["sevastopol", "bucuresti", 4, "white"],
  ["erzurum", "angora", 3, "black"],
  ["angora", "constantinople", 2, "gray", "tunnel"],
  ["angora", "smyrna", 3, "orange", "tunnel"],
  ["smyrna", "constantinople", 2, "gray", "tunnel"],
  ["smyrna", "athina", 2, "gray", 1],
  ["smyrna", "palermo", 6, "gray", 2],
  ["constantinople", "sofia", 3, "blue"],
  ["constantinople", "bucuresti", 3, "yellow"],
  ["sofia", "athina", 3, "purple"],
  ["sofia", "bucuresti", 2, "gray", "tunnel"],
  ["sofia", "sarajevo", 2, "gray", "tunnel"],
  ["athina", "sarajevo", 4, "green"],
  ["athina", "brindisi", 4, "gray", 1],
  ["bucuresti", "budapest", 4, "gray", "tunnel"],
  ["budapest", "sarajevo", 3, "purple"],
  ["budapest", "zagrab", 2, "orange"],
  ["budapest", "wien", 1, "red"],
  ["budapest", "wien", 1, "white"],
  ["wien", "zagrab", 2, "gray"],
  ["wien", "munchen", 3, "orange"],
  ["munchen", "zurich", 2, "yellow", "tunnel"],
  ["munchen", "venezia", 2, "blue", "tunnel"],
  ["zurich", "venezia", 2, "green", "tunnel"],
  ["zurich", "marseille", 2, "purple", "tunnel"],
  ["marseille", "roma", 4, "gray", "tunnel"],
  ["venezia", "roma", 2, "black"],
  ["venezia", "zagrab", 2, "gray"],
  ["zagrab", "sarajevo", 3, "red"],
  ["roma", "brindisi", 2, "white"],
  ["roma", "palermo", 4, "gray", 1],
  ["brindisi", "palermo", 3, "gray", 1],
];
export const ROUTES: Route[] = rows.map(
  ([from, to, length, color, feature], index) => {
    const pair = `${from}-${to}`;
    const siblings = rows.filter((row) => row[0] === from && row[1] === to);
    const number =
      rows.slice(0, index).filter((row) => row[0] === from && row[1] === to)
        .length + 1;
    return {
      id: `${pair}-${number}`,
      from,
      to,
      length,
      color,
      tunnel: feature === "tunnel",
      locomotives: typeof feature === "number" ? feature : 0,
      ...(siblings.length > 1 ? { parallel: pair } : {}),
    };
  },
);

const tickets: [string, string, number][] = [
  ["amsterdam", "pamplona", 7],
  ["amsterdam", "wilno", 12],
  ["angora", "kharkov", 10],
  ["athina", "angora", 5],
  ["athina", "wilno", 11],
  ["barcelona", "bruxelles", 8],
  ["barcelona", "munchen", 8],
  ["berlin", "bucuresti", 8],
  ["berlin", "moskva", 12],
  ["berlin", "roma", 9],
  ["brest", "marseille", 7],
  ["brest", "venezia", 8],
  ["bruxelles", "danzig", 9],
  ["budapest", "sofia", 5],
  ["edinburgh", "paris", 7],
  ["essen", "kyiv", 10],
  ["frankfurt", "kobenhavn", 5],
  ["frankfurt", "smolensk", 13],
  ["kyiv", "petrograd", 6],
  ["kyiv", "sochi", 8],
  ["london", "berlin", 7],
  ["london", "wien", 10],
  ["madrid", "dieppe", 8],
  ["madrid", "zurich", 8],
  ["marseille", "essen", 8],
  ["palermo", "constantinople", 8],
  ["paris", "wien", 8],
  ["paris", "zagrab", 7],
  ["riga", "bucuresti", 10],
  ["roma", "smyrna", 8],
  ["rostov", "erzurum", 5],
  ["sarajevo", "sevastopol", 8],
  ["smolensk", "rostov", 8],
  ["sofia", "smyrna", 5],
  ["stockholm", "wien", 11],
  ["venezia", "constantinople", 10],
  ["warszawa", "smolensk", 6],
  ["zagrab", "brindisi", 6],
  ["zurich", "brindisi", 6],
  ["zurich", "budapest", 6],
  ["brest", "petrograd", 20],
  ["cadiz", "stockholm", 21],
  ["edinburgh", "athina", 21],
  ["kobenhavn", "erzurum", 21],
  ["lisboa", "danzig", 20],
  ["palermo", "moskva", 20],
];
export const TICKETS: Ticket[] = tickets.map(([from, to, points]) => ({
  id: `${from}-${to}`,
  from,
  to,
  points,
  long: points >= 20,
}));
export const CITY_BY_ID = Object.fromEntries(
  CITIES.map((city) => [city.id, city]),
) as Record<string, City>;
export const ROUTE_BY_ID = Object.fromEntries(
  ROUTES.map((route) => [route.id, route]),
) as Record<string, Route>;
export const TICKET_BY_ID = Object.fromEntries(
  TICKETS.map((ticket) => [ticket.id, ticket]),
) as Record<string, Ticket>;
export const ROUTE_POINTS: Record<number, number> = {
  1: 1,
  2: 2,
  3: 4,
  4: 7,
  6: 15,
  8: 21,
};
export const PLAYER_COLORS = [
  "#de6658",
  "#489dd6",
  "#e4bc52",
  "#65a980",
  "#a685c9",
];
